import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon, HomeIcon } from '@heroicons/react/24/outline';
import { getErrorBoundaryFallback } from '../utils/errorHandler';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Process error using our error handler
    const errorFallback = getErrorBoundaryFallback(error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    const basename = window.location.pathname.startsWith('/rayyi7-dev') ? '/rayyi7-dev' : 
                     window.location.pathname.startsWith('/dev') ? '/dev' : '';
    window.location.href = basename + '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-lg w-full">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="flex justify-center mb-6">
                <div className="bg-red-100 rounded-full p-4">
                  <ExclamationTriangleIcon className="h-12 w-12 text-red-600" />
                </div>
              </div>

              <h1 className="text-2xl font-bold text-center text-gray-900 mb-4">
                عذراً، حدث خطأ غير متوقع
              </h1>
              
              <p className="text-center text-gray-600 mb-8">
                نعتذر عن الإزعاج. حدث خطأ غير متوقع في التطبيق. يرجى المحاولة مرة أخرى أو الاتصال بالدعم إذا استمرت المشكلة.
              </p>

              {/* Error details - always show for debugging */}
              {this.state.error && (
                <div className="mb-6 p-4 bg-gray-100 rounded-lg">
                  <h3 className="font-semibold text-gray-700 mb-2">Error Details:</h3>
                  <p className="text-sm text-red-600 font-mono mb-2">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <details className="text-xs text-gray-600">
                      <summary className="cursor-pointer font-semibold mb-1">
                        Component Stack
                      </summary>
                      <pre className="overflow-auto p-2 bg-white rounded mt-2">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={this.handleReload}
                  className="w-full flex items-center justify-center px-4 py-3 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666] transition-colors"
                >
                  <ArrowPathIcon className="h-5 w-5 mr-2" />
                  إعادة تحميل الصفحة
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="w-full flex items-center justify-center px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <HomeIcon className="h-5 w-5 mr-2" />
                  العودة للصفحة الرئيسية
                </button>
              </div>

              <div className="mt-8 text-center">
                <p className="text-sm text-gray-500">
                  إذا استمرت المشكلة، يرجى التواصل مع الدعم الفني
                </p>
                <a
                  href="mailto:support@rayyi7.com"
                  className="text-sm text-blue-600 hover:text-blue-700 underline"
                >
                  support@rayyi7.com
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Functional component wrapper for easier use with hooks
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) => {
  return (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
};

// Hook for error handling in functional components
export const useErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  const resetError = () => setError(null);
  const captureError = (error: Error) => setError(error);

  return { resetError, captureError };
};

export default ErrorBoundary;